import { mapValues, pick, propOr, reject } from '../../../utils/fp';
import { getDimensions } from '@dbmx/sdmx';
import { getDataflowAnnotations, getDataStructure } from '../accessors/structure';

const DEFAULT_ANNOT_TYPE = 'DEFAULT';
const DEFAULT_PARAMS_ANNOT = {
  TIME_PERIOD_START: 'startPeriod',
  TIME_PERIOD_END: 'endPeriod',
  LASTNOBSERVATIONS: 'lastNObservations',
  LASTNPERIODS: 'lastNPeriods',
};

const getRawDefaultAnnotationContent = structure => {
  const ann = getDataflowAnnotations(structure).find(a => a.type === DEFAULT_ANNOT_TYPE) ?? {};
  const title = ann?.title || '';
  return title.split(',').reduce((acc, entry) => {
    const [property, value] = entry.split('=');
    const key = propOr(property, property, DEFAULT_PARAMS_ANNOT);
    const val = !value && key === 'lastNObservations' ? 1 : value;
    return { ...acc, [key]: val };
  }, {});
};

export const getDefaultAnnotationContent = structure => {
  const defaultAnnotationContent = getRawDefaultAnnotationContent(structure);
  const dataStructure = getDataStructure(structure);
  const dimensionsIds = getDimensions(dataStructure).map(dimension => dimension.id);

  const parseDefaultSelection = content =>
    mapValues(value => value.split('+'), pick(dimensionsIds, content));

  const parseDefaultDataParams = content =>
    reject(
      value => Number(value) === 0,
      pick(['startPeriod', 'endPeriod', 'lastNObservations', 'lastNPeriods'], content),
    );

  return {
    selection: parseDefaultSelection(defaultAnnotationContent),
    params: parseDefaultDataParams(defaultAnnotationContent),
  };
};

const LAYOUT_ANNOTATIONS_TYPES = new Set(['LAYOUT_ROW', 'LAYOUT_COLUMN', 'LAYOUT_ROW_SECTION']);
const LAYOUT_KEYS = {
  LAYOUT_COLUMN: 'header',
  LAYOUT_ROW: 'rows',
  LAYOUT_ROW_SECTION: 'sections',
};

export const getDefaultTableLayout = structure =>
  getDataflowAnnotations(structure).reduce((acc, annotation) => {
    if (!LAYOUT_ANNOTATIONS_TYPES.has(annotation.type)) {
      return acc;
    }
    const ids = propOr('', 'title', annotation).split(',');

    const layoutKey = LAYOUT_KEYS[annotation.type];
    const currentIds = propOr([], layoutKey, acc);

    return {
      ...acc,
      [layoutKey]: [...currentIds, ...ids],
    };
  }, {});

const MICRODATA_TYPE = 'DRILLDOWN';
export const getMicrodataDimensionId = structure => {
  const annot = getDataflowAnnotations(structure).find(a => a.type === MICRODATA_TYPE);
  return annot?.title;
};

const EXT_RESOURCE_TYPE = 'EXT_RESOURCE';
export const getExternalResources = structure =>
  getDataflowAnnotations(structure).reduce((acc, annotation) => {
    if (annotation.type !== EXT_RESOURCE_TYPE) {
      return acc;
    }
    const split = (annotation.text || '').split('|');

    if (split.length < 2) {
      return acc;
    }
    const [label, link, img] = split;
    return [
      ...acc,
      {
        id: `EXT_RESOURCE_${acc.length}`,
        label,
        link,
        img,
      },
    ];
  }, []);

export const getOrder = (annotations = []) => {
  const orderAnnotation = annotations.find(annotation => annotation.type === 'ORDER');
  const order = orderAnnotation?.text || '-1';
  return Number(order);
};
